import { useEffect, useCallback, useMemo } from "react";
import "driver.js/dist/driver.css";
import { useTour } from "./useTour";
import {
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "@heroui/react";
import { useDisclosure } from "@heroui/react";
import useIsMobile from "@/lib/utils/useIsMobile";
import useAuth from "@/lib/auth/useAuth";
import { driverObj } from "./driverConfig";
import { DriveStep, Driver } from "driver.js";
import useFeatureFlags from "../featureFlags/useFeatureFlags";

const customStyles = `
  .driver-active-element {
    animation: pulse 2s infinite;
    box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.5);
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(66, 153, 225, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(66, 153, 225, 0);
    }
  }
`;

const TOUR_DELAYS = {
    MOBILE_MENU_TOGGLE: 400,
    MOBILE_NEXT_ELEMENT_CHECK: 700,
    MOBILE_START_DELAY: 250,
    DESKTOP_START_DELAY: 100,
    DRIVER_INIT_DELAY: 300,
} as const;

declare global {
    interface Window {
        _tourContext:
            | {
                  setCurrentTourStep: (step: number) => void;
                  toggleMobileMenu?: () => void;
              }
            | undefined;
    }
}

const mobileSteps = [
    {
        element: '[data-tour="hamburger-menu"]',
        popover: {
            title: "Navigation Menu",
            description:
                "Click this menu icon to access all the features of N-Time.",
            side: "right" as const,
            align: "start" as const,
            onNextClick: (
                _element: Element | undefined,
                _step: DriveStep,
                { driver }: { driver: Driver },
            ) => {
                if (window._tourContext?.toggleMobileMenu) {
                    const menuContainer = document.querySelector(
                        '[data-test-id="mobile-menu-container"]',
                    ) as HTMLElement;
                    const isMenuOpen =
                        menuContainer &&
                        menuContainer.style.height !== "0px" &&
                        menuContainer.style.height !== "";

                    if (!isMenuOpen) {
                        window._tourContext.toggleMobileMenu();
                    }

                    setTimeout(() => {
                        const nextElement = document.querySelector(
                            '[data-tour="home-option"]',
                        );
                        const isVisible = nextElement
                            ? isElementVisible(nextElement)
                            : false;

                        if (nextElement && isVisible && driver.isActive()) {
                            driver.moveNext();
                        } else {
                            setTimeout(() => {
                                const retryElement = document.querySelector(
                                    '[data-tour="home-option"]',
                                );
                                const retryVisible = retryElement
                                    ? isElementVisible(retryElement)
                                    : false;

                                if (
                                    retryElement &&
                                    retryVisible &&
                                    driver.isActive()
                                ) {
                                    driver.moveNext();
                                }
                            }, TOUR_DELAYS.MOBILE_NEXT_ELEMENT_CHECK);
                        }
                    }, TOUR_DELAYS.MOBILE_MENU_TOGGLE);
                }
            },
        },
    },
    {
        element: '[data-tour="home-option"]',
        popover: {
            title: "Home",
            description:
                "Quickly view & filter fields by nitrogen sufficiency.",
            side: "right" as const,
        },
    },
    {
        element: '[data-tour="fields-option"]',
        popover: {
            title: "Fields",
            description: "Access field dashboard and add new fields.",
            side: "right" as const,
        },
    },
    {
        element: '[data-tour="documentation-option"]',
        popover: {
            title: "Documentation",
            description: "Generate field performance reports.",
            side: "right" as const,
        },
    },
    {
        element: '[data-tour="network-option"]',
        popover: {
            title: "Network",
            description:
                "Add and manage users and their permissions, configure your organization.",
            side: "right" as const,
        },
    },
    {
        element: '[data-tour="settings-option"]',
        popover: {
            title: "Settings",
            description:
                "Edit your account & notifications settings, connected platforms (JD Ops, Climate FieldView, etc..), add fertilizer products.",
            side: "right" as const,
        },
    },
];

// check if menu is visible
const isElementVisible = (element: Element): boolean => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.visibility !== "hidden" &&
        style.display !== "none" &&
        style.opacity !== "0"
    );
};

export function TourGuide() {
    const { isTourActive, endTour, setCurrentTourStep } = useTour();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isMobile } = useIsMobile();
    const { userIsSentinel, user, isConsultant } = useAuth();
    const { allNEnabled } = useFeatureFlags();

    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.textContent = customStyles;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    const desktopSteps = useMemo(() => {
        const baseSteps = [
            {
                element: '[data-tour="home-icon"]',
                popover: {
                    title: "Home",
                    description:
                        "Quickly view & filter fields by nitrogen sufficiency.",
                    side: "right" as const,
                },
            },
            {
                element: '[data-tour="fields-icon"]',
                popover: {
                    title: "Fields",
                    description: "Access field dashboard and add new fields.",
                    side: "right" as const,
                },
            },
            {
                element: '[data-tour="network-icon"]',
                popover: {
                    title: "Network",
                    description:
                        "Add and manage users and their permissions, configure your organization.",
                    side: "right" as const,
                },
            },
        ];

        if (userIsSentinel || allNEnabled) {
            baseSteps.splice(
                2,
                0,

                {
                    element: '[data-tour="planner-icon"]',
                    popover: {
                        title: "Scenario Planner",
                        description:
                            "Run simulations for different weather, application, and price scenarios and get modeled crop results.",
                        side: "right" as const,
                    },
                },
            );
        }

        if (userIsSentinel) {
            baseSteps.splice(3, 0, {
                element: '[data-tour="documentation-icon"]',
                popover: {
                    title: "Documentation",
                    description: "Generate field performance reports.",
                    side: "right" as const,
                },
            });
        }

        if (userIsSentinel || (user?.is_group_admin && isConsultant)) {
            baseSteps.push({
                element: '[data-tour="enrollment-icon"]',
                popover: {
                    title: "Enrollment",
                    description:
                        "Enroll fields to enable insights, view current balance & contracts.",
                    side: "right" as const,
                },
            });
        }

        baseSteps.push({
            element: '[data-tour="settings-icon"]',
            popover: {
                title: "Settings",
                description:
                    "Edit your account & notifications settings, connected platforms (JD Ops, Climate FieldView, etc..), add fertilizer products.",
                side: "right" as const,
            },
        });

        return baseSteps;
    }, [userIsSentinel, allNEnabled, user?.is_group_admin, isConsultant]);

    useEffect(() => {
        window._tourContext = { setCurrentTourStep };
        return () => {
            window._tourContext = undefined;
        };
    }, [setCurrentTourStep]);

    useEffect(() => {
        const hasSeenWelcomeModal =
            localStorage.getItem("hasSeenWelcomeModal") === "true";

        if (isTourActive && !hasSeenWelcomeModal) {
            onOpen();
        }
    }, [isTourActive, onOpen]);

    useEffect(() => {
        driverObj.setSteps(isMobile ? mobileSteps : desktopSteps);
    }, [isMobile, desktopSteps]);

    const verifyTourElements = useCallback(
        (steps: DriveStep[]) => {
            const stepsToCheck = isMobile ? steps.slice(0, 1) : steps;

            return stepsToCheck.every((step) => {
                const elementSelector = step.element;
                if (!elementSelector) return true;

                const element = document.querySelector(
                    elementSelector as string,
                );
                return !!element;
            });
        },
        [isMobile],
    );

    const handleStartTour = useCallback(() => {
        localStorage.setItem("hasSeenWelcomeModal", "true");
        onClose();

        setTimeout(
            () => {
                const steps = isMobile ? mobileSteps : desktopSteps;
                driverObj.setSteps(steps);

                if (verifyTourElements(steps)) {
                    if (driverObj.isActive()) {
                        driverObj.destroy();
                    }

                    setTimeout(
                        () => {
                            try {
                                driverObj.drive();

                                const handleDestroy = () => {
                                    endTour();
                                    document.removeEventListener(
                                        "driver:destroy",
                                        handleDestroy,
                                    );
                                };
                                document.removeEventListener(
                                    "driver:destroy",
                                    handleDestroy,
                                );
                                document.addEventListener(
                                    "driver:destroy",
                                    handleDestroy,
                                );
                            } catch {
                                endTour();
                            }
                        },
                        isMobile
                            ? TOUR_DELAYS.DRIVER_INIT_DELAY
                            : TOUR_DELAYS.DESKTOP_START_DELAY,
                    );
                } else {
                    endTour();
                }
            },
            isMobile
                ? TOUR_DELAYS.MOBILE_START_DELAY
                : TOUR_DELAYS.DESKTOP_START_DELAY,
        );
    }, [isMobile, verifyTourElements, endTour, onClose, desktopSteps]);

    const handleSkipTour = useCallback(() => {
        localStorage.setItem("hasSeenWelcomeModal", "true");
        endTour();
        onClose();
    }, [endTour, onClose]);

    return isOpen ? (
        <Modal isOpen={isOpen} onClose={onClose} className="z-[2000]">
            <ModalContent className="z-[2000]">
                <ModalHeader>Welcome to N-Time!</ModalHeader>
                <ModalBody>
                    <p>
                        Would you like to take a quick tour of the main
                        features?
                    </p>
                    <p>
                        We'll show you around the navigation bar and help you
                        get started.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                        You can restart this tour anytime by clicking the help
                        icon (?) at the bottom of the navigation bar or from
                        your account settings page.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="danger"
                        variant="light"
                        onPress={handleSkipTour}
                    >
                        Skip Tour
                    </Button>
                    <Button color="primary" onPress={handleStartTour}>
                        Start Tour
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    ) : null;
}
